import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Container,
  Typography,
  Grid,
  Button,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Page from '../../components/Page';
import { CompanyLicenseRenewals, CompanyLicenses } from '../../client/services';
import { toast } from 'react-toastify';
import useToggle from '../../hooks/useToggle';
import UploadAssetsModal from '../../components/UploadAssetsModal';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import moment from 'moment';

interface IInitialState {
  newLicenseAssetId: string | null;
}

const renewalSchema = Yup.object().shape({
  newLicenseAssetId: Yup.string().required('Please upload new license document'),
});

const LicenseRenewal = () => {
  const { toggle: toggleUpload, onOpen: onOpenUpload, onClose: onCloseUpload } = useToggle();
  const [attachmentArray, setAttachmentArray] = useState<string[]>([]);
  const [renewalRequests, setRenewalRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [currentLicense, setCurrentLicense] = useState<any>(null);

  const {
    handleSubmit,
    setFieldValue,
    resetForm,
    isSubmitting,
    errors,
    touched,
    values,
  } = useFormik<IInitialState>({
    initialValues: {
      newLicenseAssetId: null,
    },
    validationSchema: renewalSchema,
    onSubmit: async (values) => {
      if (!values.newLicenseAssetId) {
        return;
      }

      try {
        setLoading(true);

        await CompanyLicenseRenewals.create({
          newLicenseAssetId: values.newLicenseAssetId,
        });

        toast.success('License renewal request submitted successfully');
        resetForm();
        setAttachmentArray([]);
        fetchRenewalRequests();
        fetchCurrentLicense();
      } catch (error: any) {
        console.error('Error submitting renewal request:', error);
        toast.error(error?.message || 'Failed to submit renewal request');
      } finally {
        setLoading(false);
      }
    },
  });

  const fetchRenewalRequests = async () => {
    try {
      setFetching(true);
      const response = await CompanyLicenseRenewals.find({
        query: {
          $limit: 100,
          $sort: { createdAt: -1 },
        },
      });

      setRenewalRequests(response.data || []);
    } catch (error) {
      console.error('Error fetching renewal requests:', error);
    } finally {
      setFetching(false);
    }
  };

  const fetchCurrentLicense = async () => {
    try {
      const response = await CompanyLicenses.find({
        query: {
          isActive: true,
          $limit: 1,
          $sort: { createdAt: -1 },
        },
      });

      if (response?.data && response.data.length > 0) {
        setCurrentLicense(response.data[0]);
      } else {
        setCurrentLicense(null);
      }
    } catch (error) {
      console.error('Error fetching current license:', error);
      setCurrentLicense(null);
    }
  };

  useEffect(() => {
    fetchRenewalRequests();
    fetchCurrentLicense();
  }, []);

  useEffect(() => {
    if (attachmentArray.length > 0) {
      setFieldValue('newLicenseAssetId', attachmentArray[0]);
    }
  }, [attachmentArray, setFieldValue]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Page title="License Renewal">
      <Container maxWidth="lg">
        <Box sx={{ pt: 3, pb: 2 }}>
          <Typography variant="h4">Broker License Renewal</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Upload your renewed broker license document and submit a renewal request. The admin will review your request and set the expiration date. Your account will be reactivated once approved.
          </Typography>
        </Box>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Submit Renewal Request
            </Typography>

            {/* Display Current License Information */}
            {currentLicense && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Current License Expiration Date:
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {moment(currentLicense.expirationDate).format('YYYY-MM-DD')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Grace Period End Date:
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {moment(currentLicense.expirationDate).add(90, 'days').format('YYYY-MM-DD')}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box>
                    <Button
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                      onClick={onOpenUpload}
                      sx={{ mb: 2 }}
                    >
                      Upload New License Document
                    </Button>
                    {attachmentArray.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Alert severity="success">
                          License document uploaded successfully
                        </Alert>
                      </Box>
                    )}
                    {errors.newLicenseAssetId && touched.newLicenseAssetId && (
                      <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                        {errors.newLicenseAssetId}
                      </Typography>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    loading={isSubmitting || loading}
                    disabled={!values.newLicenseAssetId}
                  >
                    Submit Renewal Request
                  </LoadingButton>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Renewal Request History
            </Typography>

            {fetching ? (
              <Box sx={{ py: 3, textAlign: 'center' }}>
                <Typography>Loading...</Typography>
              </Box>
            ) : renewalRequests.length === 0 ? (
              <Box sx={{ py: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">No renewal requests found</Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Request Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Expiration Date (Set by Admin)</TableCell>
                      <TableCell>Reviewed At</TableCell>
                      <TableCell>Rejection Reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {renewalRequests.map((request) => (
                      <TableRow key={request.id}>
                        <TableCell>
                          {moment(request.createdAt).format('YYYY-MM-DD')}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={request.status}
                            color={getStatusColor(request.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {request.requestedExpirationDate
                            ? moment(request.requestedExpirationDate).format('YYYY-MM-DD')
                            : '-'}
                        </TableCell>
                        <TableCell>
                          {request.reviewedAt
                            ? moment(request.reviewedAt).format('YYYY-MM-DD')
                            : '-'}
                        </TableCell>
                        <TableCell>
                          {request.rejectionReason || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        <UploadAssetsModal
          toggle={toggleUpload}
          onClose={onCloseUpload}
          setAttachmentArray={setAttachmentArray}
          module="trade_license"
          isSingleUpload={true}
        />
      </Container>
    </Page>
  );
};

export default LicenseRenewal;

